import { type ComponentPropsWithRef } from "react";
import { Command as CommandPrimitive } from "cmdk";
import { Search } from "lucide-react";

import { cn } from "@/lib/utils";

const Command = ({
  className,
  ref,
  ...props
}: ComponentPropsWithRef<typeof CommandPrimitive>) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      `
        flex size-full flex-col overflow-hidden rounded-md bg-popover
        text-popover-foreground
      `,
      className,
    )}
    {...props}
  />
);
Command.displayName = CommandPrimitive.displayName;

type CommandInputProps = ComponentPropsWithRef<
  typeof CommandPrimitive.Input
> & {
  /** Omit bottom border when the input is the only visible row (e.g. popover-anchored combobox). */
  omitWrapperBottomBorder?: boolean;
  /** Hide the search icon (compact fields with few options). */
  hideSearchIcon?: boolean;
  /** Classes for the outer `[cmdk-input-wrapper]` row (e.g. strip padding when an outer field shell provides it). */
  wrapperClassName?: string;
};

const CommandInput = ({
  className,
  omitWrapperBottomBorder = false,
  hideSearchIcon = false,
  wrapperClassName,
  ref,
  ...props
}: CommandInputProps) => (
  <div
    className={cn(
      "flex items-center px-3",
      !omitWrapperBottomBorder && "border-b border-input",
      hideSearchIcon && "px-2",
      wrapperClassName,
    )}
    cmdk-input-wrapper=""
  >
    {hideSearchIcon ? null : (
      <Search className="mr-2 size-4 shrink-0 opacity-50" />
    )}
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        `
          flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none
          placeholder:text-foreground/45
          disabled:cursor-not-allowed disabled:opacity-50
        `,
        className,
      )}
      {...props}
    />
  </div>
);
CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = ({
  className,
  ref,
  ...props
}: ComponentPropsWithRef<typeof CommandPrimitive.List>) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn("max-h-48 overflow-x-hidden overflow-y-auto", className)}
    {...props}
  />
);

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = ({
  ref,
  ...props
}: ComponentPropsWithRef<typeof CommandPrimitive.Empty>) => (
  <CommandPrimitive.Empty
    ref={ref}
    className="py-6 text-center text-sm"
    {...props}
  />
);

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = ({
  className,
  ref,
  ...props
}: ComponentPropsWithRef<typeof CommandPrimitive.Group>) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      `
        overflow-hidden p-1 text-foreground
        **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5
        **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium
        **:[[cmdk-group-heading]]:text-foreground/45
      `,
      className,
    )}
    {...props}
  />
);

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandItem = ({
  className,
  ref,
  ...props
}: ComponentPropsWithRef<typeof CommandPrimitive.Item>) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      `
        relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5
        text-sm outline-none select-none
        data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50
        data-[selected=true]:bg-accent
        data-[selected=true]:text-accent-foreground
        [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0
      `,
      className,
    )}
    {...props}
  />
);

CommandItem.displayName = CommandPrimitive.Item.displayName;

export {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
};

export { useCommandState } from "cmdk";
